/**
 * The completion moments of student Study (umbrella study-motion-feedback-prd.md §3, M1–M9).
 * Each function decides which effect fits the moment and plays it with studyMotion.ts.
 * Callers invoke them only after a successful save; every function is decorative, never
 * throws and plays nothing with reduced motion.
 */

import type { StudyAnswerVerdict, StudyPracticeSet } from './studyAnalysisFollowUpApi';
import {
  burst,
  confetti,
  fly,
  grow,
  nudge,
  stamp,
  studyNavTarget,
  visibleElement,
} from './studyMotion';

/** M4 (study-motion-feedback-prd.md): an added date flies to Home, where tasks and tests live. */
export function addDateMotion(index: number, kind: 'task' | 'test') {
  return fly(document.querySelector(`[data-study-date-row="${index}"]`), studyNavTarget('Home'), {
    ghost: 'card',
    label: kind === 'test' ? 'Test' : 'Task',
  });
}

/** M6 (study-motion-feedback-prd.md): a card flies to More (Review lives there), then a stamp. */
export async function reviewFlight(source: HTMLElement | null, stampLabel: string) {
  const target = studyNavTarget('More');
  if (await fly(source, target, { ghost: 'card', label: 'Review' })) await stamp(target, stampLabel);
}
/**
 * M2 / M3 (study-motion-feedback-prd.md): a correct answer bursts and a star flies into the
 * set's "tried" counter (confetti when every question in the set is correct); a wrong or
 * incomplete answer gets a gentle nudge. "Can't tell" stays quiet.
 */
export async function practiceMotion(
  verdict: StudyAnswerVerdict | null,
  button: HTMLButtonElement | null,
  set: StudyPracticeSet,
  questionId: string,
) {
  if (verdict === 'incorrect' || verdict === 'incomplete') {
    await nudge(button);
    return;
  }
  if (verdict !== 'correct') return;
  const counter = document.querySelector(`[data-study-practice-tried="${set.id}"]`);
  await Promise.all([burst(button), fly(button, counter, { ghost: 'star' })]);
  const allCorrect = set.questions.every(
    (q) => q.id === questionId || q.latest_attempt?.verdict === 'correct',
  );
  if (allCorrect) await confetti();
}
/**
 * M1 (study-motion-feedback-prd.md): finishing a question bursts at Finish and a star flies to
 * the next-question panel; confetti when the panel says every question is done.
 */
export async function finishMotion(button: HTMLButtonElement | null) {
  const next = document.querySelector('[data-testid="question-session-next"]');
  await Promise.all([burst(button), fly(button, next, { ghost: 'star' })]);
  if (next?.textContent?.includes('All questions done')) await confetti();
}
const NOTE_FLIGHT_PAGES = 3;
const NOTE_FLIGHT_GAP_MS = 120;

/** M5: up to three page thumbnails fly one after another into the Materials link. */
export function notesSavedMotion(section: HTMLElement | null) {
  const target = studyNavTarget('Materials');
  const thumbs = Array.from(section?.querySelectorAll('img') ?? []).slice(0, NOTE_FLIGHT_PAGES);
  const sources = thumbs.length > 0 ? thumbs : [section];
  sources.forEach((source, index) => {
    void fly(source, target, { ghost: 'sheet', delayMs: index * NOTE_FLIGHT_GAP_MS });
  });
}
/** M7: the star bursts, then flies to "My favourites" when that tab is on screen. */
export async function favouriteMotion(key: string) {
  const star = document.querySelector(`[data-study-favourite="${key}"]`);
  const tab = visibleElement(['[data-study-favourites-tab]']);
  const rect = tab?.getBoundingClientRect();
  const onScreen = !!rect && rect.bottom > 0 && rect.top < window.innerHeight;
  await Promise.all([burst(star), onScreen ? fly(star, tab, { ghost: 'star' }) : Promise.resolve(false)]);
}
const GROW_AFTER_FIT_MS = 120;
const GROW_STAGGER_MS = 60;
const MAX_GROWN_NODES = 12;

/**
 * M8 (study-motion-feedback-prd.md): new ideas grow out of the anchor idea one after another,
 * after fitView has placed them. Decorative only; skipped with reduced motion.
 */
export function growNewNodes(ids: string[], anchorId: string | null | undefined) {
  const node = (id: string) => document.querySelector(`.react-flow__node[data-id="${id}"]`);
  ids.slice(0, MAX_GROWN_NODES).forEach((id, index) => {
    setTimeout(
      () => void grow(node(id), anchorId ? node(anchorId) : null),
      GROW_AFTER_FIT_MS + index * GROW_STAGGER_MS,
    );
  });
}
const WRAP_UP_MOTION_MAX_MS = 1200;

/**
 * M9 (study-motion-feedback-prd.md): confetti and the summary flies to Home before the page
 * moves there. Navigation waits at most WRAP_UP_MOTION_MAX_MS; without motion it is instant.
 */
export async function wrapUpMotion() {
  const summary = document.querySelector('[data-study-wrapup-summary]');
  const played = Promise.all([
    confetti(),
    fly(summary, studyNavTarget('Home'), { ghost: 'card', label: 'Next Term' }),
  ]);
  await Promise.race([
    played,
    new Promise((resolve) => setTimeout(resolve, WRAP_UP_MOTION_MAX_MS)),
  ]);
}
